import { auth } from '../firebase/config';

class StrokeScoringService {
  constructor() {
    // Check if we're in development by looking at the URL
    const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

    // Check for URL parameter override (e.g., ?useRemoteAPIs=true)
    const urlParams = new URLSearchParams(window.location.search);
    const useRemoteAPIs = urlParams.get('useRemoteAPIs') === 'true';

    const envSuffix = import.meta.env.PROD ? 'prod' : 'dev';
    this.baseUrl = (isDevelopment && !useRemoteAPIs)
      ? 'http://localhost:8006'  // Local stroke-scorer API
      : `https://stroke-scorer-api-${envSuffix}.blackbay-6bd764e8.eastus2.azurecontainerapps.io`;
  }

  /**
   * Get auth headers with Firebase ID token
   */
  async _getAuthHeaders() {
    const headers = { 'Content-Type': 'application/json' };
    const user = auth.currentUser;
    if (user) {
      const idToken = await user.getIdToken();
      headers['X-Firebase-Token'] = idToken;
    }
    return headers;
  }

  /**
   * Re-run score prediction over strokes whose in/out calls have been reviewed.
   *
   * @param {Array} strokes - Stroke records carrying the labeller's in/out calls
   * @param {Object} matchInfo - Optional match metadata, normally from a job request file
   * @param {Array} matchInfo.setGameScores - Completed game scores per set, e.g. [[4, 6], [6, 3]]
   * @param {string} matchInfo.initialTopPlayerId - Player starting at the top of the court
   * @param {string} matchInfo.initialBottomPlayerId - Player starting at the bottom of the court
   * @param {boolean} matchInfo.adRule - Whether the match uses ad scoring
   * @returns {Promise<{strokes: Array, metrics: Object, warnings: Array}>}
   */
  async rescore(strokes, matchInfo = {}) {
    const headers = await this._getAuthHeaders();
    const response = await fetch(`${this.baseUrl}/api/rescore`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        strokes,
        set_game_scores: matchInfo.setGameScores ?? null,
        initial_top_player_id: matchInfo.initialTopPlayerId ?? null,
        initial_bottom_player_id: matchInfo.initialBottomPlayerId ?? null,
        ad_rule: matchInfo.adRule ?? false
      })
    });

    // The service reports validation problems in the body, so prefer its
    // message over a bare status code.
    const data = await response.json().catch(() => null);

    if (!response.ok || !data?.success) {
      throw new Error(data?.error || `Re-scoring failed with status ${response.status}`);
    }

    return {
      strokes: data.strokes,
      metrics: data.metrics || {},
      warnings: data.warnings || []
    };
  }
}

const strokeScoringService = new StrokeScoringService();
export default strokeScoringService;
